// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../global.d.ts" />
import { PlayerId } from '@suntex.fim/api-client';
import { UpdateRequest, hashUpdateRequest } from '@suntex.fim/utils';

// Helper function to validate if a player ID is valid
export function isValidPlayerId(
  playerId?: number | null,
): playerId is PlayerId {
  return typeof playerId === 'number' && playerId > 0;
}

/**
 * Initialises the per-page-load request-signing globals that embedded game modules
 * rely on (`window.salter` and `window[salt]`).
 *
 * A cryptographically random 16-byte hex salt is generated fresh on each call,
 * giving a new salt for every page load / app startup.
 *
 * Call this once at application startup, before any embedded modules load.
 */
export function registerSalter(): void {
  // eslint-disable-next-line no-magic-numbers
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // eslint-disable-next-line no-magic-numbers
  const salt = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );

  window.salter = () => salt;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  (window as any)[salt] = (request: UpdateRequest) =>
    hashUpdateRequest(request, salt);
}

export interface RegisterWindowGlobalsOptions {
  /** Invoked when embedded module content requests navigation back to the menu. */
  backToMenu: () => void;
}

let windowGlobalsRegistered = false;

/**
 * Initialises the window globals that embedded (Adobe Animate / CreateJS)
 * module code relies on: the time-on-task callbacks, `window.backToMenu`,
 * and the request-signing salt (via `registerSalter`).
 *
 * Must be called exactly once at application startup (module scope), before
 * any embedded modules load, since some modules invoke these globals during
 * initialization via a short `setTimeout` and expect them to already be
 * defined regardless of React's render timing.
 */
export function registerWindowGlobals({
  backToMenu,
}: RegisterWindowGlobalsOptions): void {
  if (windowGlobalsRegistered) {
    // eslint-disable-next-line no-console
    console.warn(
      'registerWindowGlobals() was called more than once. It should only be called once at application startup.',
    );
  }
  windowGlobalsRegistered = true;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  window.startTimeOnTask = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  window.stopTimeOnTask = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  window.postTimeOnTaskByStatus = () => {};
  window.backToMenu = backToMenu;

  registerSalter();
}
